import _ from "lodash";
import ACLStore from "lib/aclStore";

import ACLStorage from "lib/aclService";

import { getUserCurriculumPrograms } from "modules/CommonSelectors";

import {
  INTEGRATIONS_PROGRAMME_LEVEL_VIEW_AND_MANAGE_PERM,
  INTEGRATIONS_VIEW_AND_MANAGE_PERM,
  ENABLE_PROGRAM_ROLES_FOR_INTEGRATIONS,
} from "Constants/permissionConstants";

import {
  CONTAIN_ERRORS,
  CONTAIN_HARD_ERRORS,
  FAILED,
  MODULE_JOBS_MAP,
  SYNC_FAILED,
  REJECTED,
  GETTING_SIS_DATA,
  UP_TO_DATE,
  MAP_STATUS_TO_CATEGORY,
} from "./IntegrationsConstants";

export const getUiNames = ({ data, t, localeLabels }) => {
  const CONVERT_NAMES = {
    ["PROCESSING"]: t("common:processing_no_dots"),
    ["SYNC_FAILED"]: t("common:failed"),
    ["COMPLETED"]: t("common:completed"),
    ["REJECTED"]: t("integration:rejected"),
    ["VALIDATING"]: t("common:processing_no_dots"),
    ["CANCELLED"]: t("common:cancelled"),
    ["UPTODATE"]: t("integration:uptodate"),
    ["WONDE"]: "Wonde",
    ["VERACROSS"]: "Veracross",
    ["VERACROSSV3"]: "Veracross",
    ["ISAMS"]: "iSAMS",
    ["POWERSCHOOL"]: "PowerSchool",
    ["ENGAGE"]: "Engage",
    ["CLEVER"]: "Clever",
    ["SHEET"]: "Sheet",
    ["LEX"]: "Lex",
    ["QUICKSCHOOLS"]: "Quickschools",
    ["OPENAPPLY"]: "OpenApply",
    ["CLASSLINK"]: "ClassLink",
    ["SCHOOLBASE"]: "SchoolBase",
    ["ENS"]: "ENS",
    ["COMPASS"]: "Compass",
    ["TASS"]: "TASS",
    ["DEMOSIS"]: "Demosis",
    ["CLICKEDU"]: "Clickedu",
    ["EDUKA"]: "Eduka",
    ["EDADMIN"]: "Ed-admin",
    ["BLACKBAUD"]: "Blackbaud",
    ["CIVICA"]: "Civica",
    ["HUBMIS"]: "Hubmis",
    ["GENESIS"]: "Genesis",
    ["FACTS"]: "Facts",
    ["CLASSTER"]: "Classter",
    ["EDSEMBLI"]: "Edsembli",
    ["D6"]: "D6",
    ["POWERSCHOOLV2"]: "PowerSchool v2",
    ["REALTIME"]: "Realtime",
    ["MSAOS"]: "AoS",
    ["AWAITING_APPROVAL"]: t("integration:awaiting_approval"),
    ["GETTING_SIS_DATA"]: t("integration:getting_sis_data", localeLabels),
    ["PENDING"]: t("common:pending"),
    ["ARRIVED"]: t("integration:arrived"),
    ["CREATE"]: t("common:create"),
    ["UPDATE"]: t("common:update"),
    ["DELETE"]: t("common:delete"),
    ["APPROVAL_FROM_TECH_INVESTIGATOR_PENDING"]: t(
      "integration:tech_investigator_pending"
    ),
    ["CONTAIN_ERRORS"]: t("integration:sync_contain_errors"),
    ["POST_PROCESSING_PENDING"]: t("integration:post_processing_pending"),
    ["AWAITING_TASK_CREATION"]: t("common:pending"),
    ["AWAITING_SYNC_CALL"]: t("common:pending"),
    ["SYNCHING_SUBJECTS"]: t("integration:syncing_subjects"),
    ["POST_PROCESSING_QUEUED"]: t("integration:post_processing_pending"),
    ["TASK_CREATION_QUEUED"]: t("common:pending"),
    ["SYNCHING_SUBJECTS_QUEUED"]: t("integration:syncing_subjects"),
  };

  return CONVERT_NAMES[data];
};

export const customOrder = [
  "CLASS_AND_PEOPLE_ROSTER",
  "SCHEDULE",
  "ATTENDANCE",
  "TERM_GRADE",
];

export const getSyncTypeFeatureOptions = ({ t, serviceName }) => {
  return [
    {
      value: "PULL",
      label: t("integration:service_to_toddle_with_service", {
        service: serviceName,
      }),
    },
    {
      value: "PUSH",
      label: t("integration:toddle_to_service_with_service", {
        service: serviceName,
      }),
    },
  ];
};

export const getUpdatedFeatures = ({
  features,
  activeFeatures,
  selectedFeatures,
  serviceCurriculumMapId,
  syncType,
  initialState,
}) => {
  return _.reduce(
    features,
    (acc, feature) => {
      const isRemoved =
        _.includes(activeFeatures, feature) &&
        !_.includes(selectedFeatures, feature);

      if (isRemoved) {
        acc.push({
          serviceCurriculumMapId,
          feature,
          isActive: false,
        });
      } else if (_.includes(selectedFeatures, feature)) {
        // Add feature if it’s newly selected or syncType has changed
        if (
          !_.includes(activeFeatures, feature) ||
          (feature === "ATTENDANCE" && syncType !== initialState)
        ) {
          acc.push({
            serviceCurriculumMapId,
            feature,
            isActive: true,
            settings: {
              syncType: feature === "ATTENDANCE" ? syncType : null,
            },
          });
        }
      }
      return acc;
    },
    []
  );
};

export const JOB_TABLE_CREATED_AT_FORMAT = "Do MMM YYYY, h:mm A";

export const manageSyncAlertContainerStyle = {
  width: "100%",
  marginBottom: "28px",
};

export const getSyncJobStatusConfig = ({ status, localeLabels, t }) => {
  const mappedStatusCategory = MAP_STATUS_TO_CATEGORY?.[status];

  switch (mappedStatusCategory) {
    case "AWAITING_APPROVAL":
      return {
        label: t("integration:awaiting_approval"),
        textColor: "--text-semantic-warning",
      };
    case "PROCESSING":
      return {
        label: t("common:processing_no_dots"),
        textColor: "--text-semantic-info",
      };
    case "COMPLETED":
      return {
        label: t("common:completed"),
        textColor: "--text-semantic-success",
      };
    case "REJECTED":
      return {
        label: t("integration:rejected"),
        textColor: "--text-semantic-error",
      };
    case "UPTODATE":
      return {
        label: t("integration:uptodate"),
        textColor: "--text-semantic-success",
      };
    case "FAILED":
      return { label: t("common:failed"), textColor: "--text-semantic-error" };
    case "GETTING_SIS_DATA":
      return {
        label: t("integration:getting_sis_data_with_service", localeLabels),
        textColor: "--text-semantic-info",
      };
    case "CANCELLED":
      return {
        label: t("common:cancelled"),
        textColor: "--text-semantic-error",
      };
    default:
      return { label: status, textColor: "--text-semantic-info" };
  }
};

export const mapStatusJobFeedFilters = {
  SYNC_FAILED: [CONTAIN_ERRORS, CONTAIN_HARD_ERRORS, FAILED, SYNC_FAILED],
};

export const getJobTypeToTextMap = ({ t, jobType, isOldModule }) => {
  switch (jobType) {
    case "SYNC": {
      return t("integration:roster");
    }
    case "SYNC_CALENDAR": {
      return isOldModule ? t("common:timetable") : t("integration:schedule");
    }
    case "SYNC_ATTENDANCE_V2":
    case "SYNC_ATTENDANCE":
    case "PUSH_BACK_ATTENDANCE":
    case "PUSH_BACK_ATTENDANCE_V2": {
      return t("integration:attendance");
    }
    case "PUSH_STUDENT_FINAL_GRADE":
    case "PULL_STUDENT_FINAL_GRADE": {
      return t("integration:final_grade_sync");
    }
    case "PUSH_ASSESSMENT_SYNC":
    case "PULL_ASSESSMENT_SYNC": {
      return t("integration:assignment_sync");
    }
  }
};

export const getSyncButtonJobDisabledTooltip = ({
  syncStatus,
  t,
  isOldModule,
  curriculumId,
  isCurrentModuleJobPending,
}) => {
  if (
    curriculumId === syncStatus.curriculumProgramId &&
    isCurrentModuleJobPending
  ) {
    if (syncStatus.status === "AWAITING_APPROVAL") {
      return t("integration:please_accept_or_reject_sync");
    }
    return t("integration:please_wait_for_sync_to_complete");
  }

  return syncStatus.status
    ? t(
        "integration:a_sync_is_aleady_in_progress_with_curriculum_and_feature",
        {
          feature: getJobTypeToTextMap({
            t,
            jobType: syncStatus.jobType,
            isOldModule,
          }),
          curriculum: syncStatus.acronym,
        }
      )
    : null;
};

export const getServiceName = ({ t, service }) =>
  service === "VERACROSSV3" ? "Veracross" : getUiNames({ data: service, t });

export const getErrorMessage = ({ code, message }) => {
  switch (code) {
    case "IER-000":
      return message;
    case "IER-001":
      return "Authentication Failed";
    case "IER-002":
      return "Roster sync hasn't been performed even once. Please run a roster sync.";
    case "IER-003":
      return "Period mapping issue";
    case "IER-004":
      return "Grades having multiple routines";
    case "IER-005":
      return "Operational day not matching";
    case "IER-006":
      return "Period mapping issue";
    case "IER-007":
      return "Timetable sync hasn't been performed yet. Please sync the timetable.";
    case "IER-008":
      return "Attendance categories don't match. Please complete attendance category mapping.";
    default:
      return message || "Something went wrong";
  }
};

export const errorColumns = [
  {
    label: "Sr. no.",
    id: "sr_no",
    type: "TEXTCOLUMN",
  },
  {
    label: "Error Description",
    id: "error",
    type: "TEXTCOLUMN",
  },
];

export const getErrorsFeedData = ({ errors }) => {
  return _.map(errors, (error, index) => ({
    sr_no: index + 1,
    error: getErrorMessage(error),
  }));
};

export const findModuleByJobType = jobType => {
  return (
    _.findKey(MODULE_JOBS_MAP, moduleSpecificJobTypes =>
      _.includes(moduleSpecificJobTypes, jobType)
    ) || null
  );
};

export const getIsJobAllowedToBeDeleted = ({ status }) => {
  return _.includes(
    [REJECTED, GETTING_SIS_DATA, SYNC_FAILED, UP_TO_DATE],
    status
  );
};

export const getMappingStateFiltersList = ({ t }) => {
  return [
    {
      label: t("common:all"),
      value: "ALL",
      key: "ALL",
    },
    {
      label: t("integration:mapped"),
      value: "MAPPED",
      key: "MAPPED",
    },
    {
      label: t("integration:unmapped"),
      value: "UNMAPPED",
      key: "UNMAPPED",
    },
  ];
};

export const getIsGradebookSyncForArchivedCoursesEnabled = () => {
  return ACLStore.can("AdminPortal:ArchivedCoursesGradebookSync");
};

export const curriculumIdsForWhichNodeIsVisible = ({
  permission,
  curriculumProgramIds,
}) => {
  return _.filter(curriculumProgramIds, curriculumProgramId => {
    return ACLStorage.getModuleSettingsValueByContext(permission, [
      { entityType: "CURRICULUM_PROGRAM", entityId: curriculumProgramId },
    ]);
  });
};

export const getValidIntegrationsCurriculumProgramIds = () => {
  const userCurriculumPrograms = getUserCurriculumPrograms();

  const isVisibleOnAccountLevel = ACLStorage.getModuleSettingsValueByContext(
    INTEGRATIONS_VIEW_AND_MANAGE_PERM,
    []
  );

  if (isVisibleOnAccountLevel) {
    return {
      curriculumProgramIds: _.map(userCurriculumPrograms, "id"),
      isVisibleOnAccountLevel,
    };
  }

  const validCurriculumProgramIds = _.reduce(
    [INTEGRATIONS_PROGRAMME_LEVEL_VIEW_AND_MANAGE_PERM],
    (res, permission) => {
      const curriculumIds = curriculumIdsForWhichNodeIsVisible({
        permission,
        curriculumProgramIds: _.map(userCurriculumPrograms, "id"),
      });

      return [...res, ...curriculumIds];
    },
    []
  );

  return {
    curriculumProgramIds: validCurriculumProgramIds,
    isVisibleOnAccountLevel: false,
  };
};

export const getIsProgramBasedRnpForIntegrationsEnabled = () => {
  return ACLStore.can(ENABLE_PROGRAM_ROLES_FOR_INTEGRATIONS);
};

/**
 * Filters curriculum list based on user access permissions
 * @param {Array} curriculumList - List of curriculum programs to filter
 * @param {Object} options - Additional options
 * @param {String} options.idField - The field name that contains the curriculum ID (default: 'id')
 * @returns {Object} - Object containing filtered list and empty state flag
 */
export const filterCurriculumListByAccess = (curriculumList, options = {}) => {
  const { idField = "id" } = options;

  if (_.isEmpty(curriculumList)) {
    return {
      filteredCurriculumList: [],
      isEmptyState: true,
    };
  }

  const programBasedRnpForIntegrationsEnabled = getIsProgramBasedRnpForIntegrationsEnabled();

  const {
    curriculumProgramIds: curriculumsUserHaveAccessTo,
    isVisibleOnAccountLevel,
  } = getValidIntegrationsCurriculumProgramIds();

  const filteredCurriculumList = curriculumList.filter(curriculumProgram => {
    const curriculumId = curriculumProgram[idField];
    return (
      isVisibleOnAccountLevel ||
      !programBasedRnpForIntegrationsEnabled ||
      curriculumsUserHaveAccessTo.includes(curriculumId)
    );
  });

  return {
    filteredCurriculumList,
    isEmptyState: _.isEmpty(filteredCurriculumList),
  };
};

export const getCurriculumsForSync = ({
  curriculums,
  activeTab,
  curriculumProgramId,
}) => {
  return activeTab === "schoolSettingsTab"
    ? _.filter(
        curriculums,
        curriculum => curriculum.curriculumId === curriculumProgramId
      )
    : curriculums;
};
